import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Select } from "components/base/Form";
import SearchBar from "components/base/SearchBar";
import { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { useModals } from "ModalProvider";
import Status, {
  SHIPMENT_STATUSES,
} from "pages/shipment/components/ShipmentStatus.js";
import ShipmentTitle from "./ShipmentTitle";
import queryString from "query-string";
import { useEffect, useState } from "react";
import { formatDate } from "react-day-picker/moment";
import { BsExclamationCircle, BsInfoCircle } from "react-icons/bs";
import { MdClear, MdWarningAmber } from "react-icons/md";
import { RiSubtractLine } from "react-icons/ri";
import {
  TbArrowsSort,
  TbSortAscending,
  TbSortDescending,
} from "react-icons/tb";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { FETCH_SHIPMENT_LIST } from "./graphql";
import CustomerSelector from "./CustomerSelector";
import ChooseOdooObjectsView from "../odoo/ChooseOdooObjectView";
import ConfirmOdooObjectView from "../odoo/ConfirmOdooObjectView";
import ShipmentStatusOptions from "./ShipmentStatusOptions";

const PAGE_SIZE = 30;
const DEFAULT_FILTERS = {
  range: "1y",
  status: "",
  customer: "",
  invoice: "",
  product: "",
  offset: 0,
};

function getFilters(search) {
  const params = queryString.parse(search);
  const value = (key) => (typeof params[key] === "string" ? params[key] : "");

  return {
    range: ["1y", "3y", "all"].includes(value("range")) ? value("range") : "1y",
    status: value("status"),
    customer: value("customer"),
    invoice: value("invoice"),
    product: value("product"),
    offset: Number.parseInt(value("offset"), 10) || 0,
  };
}

function getFilterSearch(filters) {
  return queryString.stringify({
    range: filters.range,
    ...(filters.status && { status: filters.status }),
    ...(filters.customer && { customer: filters.customer }),
    ...(filters.invoice && { invoice: filters.invoice }),
    ...(filters.product && { product: filters.product }),
    ...(filters.offset && { offset: filters.offset }),
  });
}

const localDateTime = (datetimeString) => {
  const d = new Date(datetimeString);
  return d.toLocaleString();
};

function computeShipment(shipment) {
  const isDelayed =
    shipment.latestStatus === "in production" &&
    new Date(shipment.etd) < new Date();
  const shouldBeShipButNot =
    shipment.latestStatus === "delivered to port" &&
    new Date(shipment.latestEtd) < new Date();
  return { ...shipment, isDelayed, shouldBeShipButNot };
}

function ShipmentList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [filters, setFilters] = useState(() => getFilters(location.search));
  const [searchValues, setSearchValues] = useState(() => ({
    customer: filters.customer,
    invoice: filters.invoice,
    product: filters.product,
  }));
  const [cargoReadyDateOrder, setCargoReadyDateOrder] = useState(null);
  const { data, loading, error } = useQuery(FETCH_SHIPMENT_LIST, {
    variables: {
      latestStatusIn: filters.status ? [filters.status] : null,
      options: {
        range: filters.range,
        customer: filters.customer,
        invoice: filters.invoice,
        product: filters.product,
        limit: PAGE_SIZE,
        offset: filters.offset,
        orderBy: cargoReadyDateOrder,
      },
    },
    fetchPolicy: "network-only",
    onError() {
      Alert("Fail to fetch shipments.");
    },
  });

  useEffect(() => {
    navigate(`/shipment/shipments?${getFilterSearch(filters)}`, {
      replace: true,
    });
  }, [filters, navigate]);

  const debouncedSetSearchFilter = useDebouncedCallback((name, value) => {
    setFilters((current) => ({ ...current, [name]: value.trim(), offset: 0 }));
  }, 1000);
  const chooseCustomerModal = useModals();
  const chooseOdooObjectsModal = useModals();
  const confirmOdooObjectModal = useModals();

  function tryCreateShipment() {
    chooseCustomerModal.present({
      title: "Choose Customer",
      children: <CustomerSelector onSelect={didChooseCustomer} />,
    });
  }

  function didChooseCustomer(customer) {
    chooseOdooObjectsModal.present({
      title: "Sales Order or Transfer",
      subtitle: customer.name,
      children: (
        <ChooseOdooObjectsView
          odooPartnerId={customer.odooPartnerId}
          onSelect={(item) => didChooseOdooObject(customer, item)}
        />
      ),
    });
  }

  function didChooseOdooObject(customer, item) {
    confirmOdooObjectModal.present({
      title: item.name,
      subtitle: "Choose product and qty to create shipment invoice",
      maxWidth: "max-w-5xl",
      children: (
        <ConfirmOdooObjectView
          item={item}
          customer={customer}
          type="CREATE_SHIPMENT"
          complete={didCreateShipment}
        />
      ),
    });
  }

  function didCreateShipment(shipment) {
    navigate(`/shipment/shipments/${shipment.id}`);
  }

  function updateFilter(name, value) {
    setFilters((current) => ({ ...current, [name]: value, offset: 0 }));
  }

  function updateSearchFilter(name, value) {
    setSearchValues((current) => ({ ...current, [name]: value }));
    debouncedSetSearchFilter(name, value);
  }

  function clearFilters() {
    debouncedSetSearchFilter.cancel();
    setFilters(DEFAULT_FILTERS);
    setSearchValues({ customer: "", invoice: "", product: "" });
  }

  // if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const shipmentsToShow = data
    ? data.shipments.results.map(computeShipment)
    : [];

  return (
    <div className="animate-fadeInDown">
      <div className="m-4 space-y-3 lg:m-6">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            className="h-[30px] items-center text-sm lg:mr-6"
            onClick={tryCreateShipment}
          >
            + Create Shipment
          </Button>
          <label className="flex items-center gap-2 text-sm">
            <span>Created</span>
            <Select
              value={filters.range}
              className="w-40"
              onChange={(event) => updateFilter("range", event.target.value)}
            >
              <option value="1y">Last year</option>
              <option value="3y">Last 3 years</option>
              <option value="all">All history</option>
            </Select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span>Status</span>
            <ShipmentStatusOptions
              value={filters.status}
              className="w-48"
              disabled={loading}
              onSelected={(value) => updateFilter("status", value)}
            />
          </label>
        </div>

        <div>
          <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
            <div className="flex items-center gap-2">
              <SearchBar
                containerClassName="flex-1 lg:w-52 lg:flex-none"
                className="placeholder-gray-500 dark:placeholder-gray-400"
                value={searchValues.customer}
                onChange={(value) => updateSearchFilter("customer", value)}
                placeholder="Customer name"
              />
              <span className="text-xs text-gray-500">AND</span>
            </div>
            <div className="flex items-center gap-2">
              <SearchBar
                containerClassName="flex-1 lg:w-52 lg:flex-none"
                className="placeholder-gray-500 dark:placeholder-gray-400"
                value={searchValues.invoice}
                onChange={(value) => updateSearchFilter("invoice", value)}
                placeholder="SO / INT"
              />
              <span className="text-xs text-gray-500">AND</span>
            </div>
            <div className="flex items-center gap-2">
              <SearchBar
                containerClassName="flex-1 lg:w-52 lg:flex-none"
                className="placeholder-gray-500 dark:placeholder-gray-400"
                value={searchValues.product}
                onChange={(value) => updateSearchFilter("product", value)}
                placeholder="Product name / Item #"
              />
            </div>
            {filters.range !== "1y" ||
            filters.status ||
            filters.customer ||
            filters.invoice ||
            filters.product ? (
              <button
                type="button"
                className="flex h-[30px] items-center gap-1 justify-self-start whitespace-nowrap text-sm text-sky-600 hover:underline lg:justify-self-auto"
                onClick={clearFilters}
              >
                <MdClear size={16} />
                Clear filters
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-10">
          <InlineSpinner />
        </div>
      ) : (
        <div className="card m-4 flex h-[70vh] flex-col overflow-hidden p-0 lg:m-6 lg:h-[80vh]">
          <div className="min-h-0 flex-1 overflow-auto whitespace-nowrap">
            <table>
              <thead className="sticky top-0 z-10 bg-gray-200 shadow-sm dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 min-w-[320px]">Shipment</th>
                  <th>
                    <div className="group relative inline-flex items-center gap-1">
                      <span>Status</span>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        aria-label="Shipment status legend"
                      >
                        <BsInfoCircle size={14} />
                      </button>
                      <div
                        role="tooltip"
                        className="pointer-events-none absolute left-0 top-full z-20 mt-2 w-56 rounded-md border border-gray-200 bg-white p-3 text-left text-xs font-normal normal-case shadow-lg opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 dark:border-gray-700 dark:bg-gray-800"
                      >
                        <div className="space-y-1">
                          {SHIPMENT_STATUSES.map((status) => (
                            <div
                              key={status}
                              className="flex items-center gap-2"
                            >
                              <Status status={status} iconOnly />
                              <span className="capitalize">{status}</span>
                            </div>
                          ))}
                          <div className="mt-2 flex items-start gap-2 border-t border-gray-200 pt-2 text-pink-500 dark:border-gray-700">
                            <BsExclamationCircle
                              size={16}
                              className="mt-0.5 shrink-0"
                            />
                            <span>Expected to be shipping by now.</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </th>
                  <th>
                    <div className="group relative inline-flex items-center gap-1">
                      <span>QC</span>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        aria-label="QC report legend"
                      >
                        <BsInfoCircle size={14} />
                      </button>
                      <div
                        role="tooltip"
                        className="pointer-events-none absolute left-0 top-full z-20 mt-2 w-52 rounded-md border border-gray-200 bg-white p-3 text-left text-xs font-normal normal-case shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 dark:border-gray-700 dark:bg-gray-800"
                      >
                        <div className="flex items-center gap-2 text-red-500">
                          <MdWarningAmber size={16} />
                          <span>Missing QC Report</span>
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <RiSubtractLine size={16} className="text-gray-500" />
                          <span>QC Report is complete</span>
                        </div>
                      </div>
                    </div>
                  </th>
                  <th>Shipping Term</th>
                  <th className="px-6">ETD</th>
                  <th className="px-6">ETA</th>
                  <th className="px-6 whitespace-nowrap text-center">
                    <div className="inline-flex items-center gap-1">
                      <span>Cargo Ready Date</span>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        aria-label="Sort by Cargo Ready Date"
                        onClick={() => {
                          setFilters((current) => ({ ...current, offset: 0 }));
                          setCargoReadyDateOrder((order) =>
                            order === null
                              ? "-requestCargoReadyDate"
                              : order === "-requestCargoReadyDate"
                                ? "requestCargoReadyDate"
                                : null,
                          );
                        }}
                      >
                        {cargoReadyDateOrder === "requestCargoReadyDate" ? (
                          <TbSortAscending size={16} />
                        ) : cargoReadyDateOrder === "-requestCargoReadyDate" ? (
                          <TbSortDescending size={16} />
                        ) : (
                          <TbArrowsSort size={16} />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-6 text-right">Created</th>
                </tr>
              </thead>
              <tbody>
                {shipmentsToShow.map((s, index) => (
                  <tr
                    key={s.id}
                    className="border-y border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900 dark:text-gray-100 animate-fadeInDown"
                    style={{
                      animationDelay: `${index * 0.015}s`,
                    }}
                    onClick={() =>
                      navigate(`/shipment/shipments/${s.id}${location.search}`)
                    }
                  >
                    <td className="px-6 py-4 whitespace-normal min-w-[320px]">
                      <div className={!s.isEffective ? "opacity-50" : ""}>
                        <ShipmentTitle
                          id={s.id}
                          customerName={s.customer.name}
                          number={s.invoiceNumber}
                          po={s.po}
                          note={s.note}
                          listRowClickable
                          showId={false}
                        />
                        <div>{s.shipContainer}</div>
                      </div>
                    </td>

                    <td className="align-middle">
                      <div className="flex space-x-4">
                        <Status status={s.latestStatus} />
                        {s.shouldBeShipButNot ? (
                          <div
                            className="group relative flex items-center text-pink-500"
                            aria-label="Expected to be shipping by now."
                          >
                            <BsExclamationCircle size={18} />
                            <span
                              role="tooltip"
                              className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1 w-64 -translate-x-1/2 rounded bg-gray-900 px-2 py-1 text-center text-xs text-white opacity-0 group-hover:opacity-100 dark:bg-gray-100 dark:text-gray-900"
                            >
                              Expected to be shipping by now.
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="align-middle">
                      {s.missingReport ? (
                        <span
                          className="group relative inline-flex items-center justify-center align-middle text-red-500"
                          aria-label="Missing QC Report"
                        >
                          <MdWarningAmber size={16} />
                          <span
                            role="tooltip"
                            className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 dark:bg-gray-100 dark:text-gray-900"
                          >
                            Missing QC Report
                          </span>
                        </span>
                      ) : (
                        <span
                          className="group relative inline-flex items-center justify-center align-middle text-gray-500"
                          aria-label="QC Report is complete"
                        >
                          <RiSubtractLine size={16} />
                          <span
                            role="tooltip"
                            className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 dark:bg-gray-100 dark:text-gray-900"
                          >
                            QC Report is complete
                          </span>
                        </span>
                      )}
                    </td>
                    <td>{s.shippingTerm}</td>
                    <td
                      className={`align-middle ${s.isDelayed ? "text-red-500 font-bold" : ""}`}
                    >
                      {s.latestEtd}
                    </td>
                    <td className="align-middle">{s.latestEta}</td>
                    <td className="text-center align-middle">
                      {s.requestCargoReadyDate
                        ? formatDate(
                            s.requestCargoReadyDate * 1000,
                            "YYYY-MM-DD",
                          )
                        : "-"}
                    </td>
                    <td className="px-6 text-xs text-right opacity-70">
                      {localDateTime(s.created)}
                      <br />
                      {s.creator.email}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="shrink-0 border-t border-gray-100 px-4 py-3 flex items-center justify-start space-x-2 text-sm opacity-60 dark:border-gray-700">
            {filters.offset > 0 ? (
              <Button
                prev
                onClick={() =>
                  setFilters((current) => ({
                    ...current,
                    offset: current.offset - PAGE_SIZE,
                  }))
                }
              />
            ) : null}
            <div>
              {filters.offset + 1}-
              {Math.min(PAGE_SIZE + filters.offset, data.shipments.total)} /{" "}
              {data.shipments.total}
            </div>
            {data.shipments.total > filters.offset + PAGE_SIZE ? (
              <Button
                next
                onClick={() =>
                  setFilters((current) => ({
                    ...current,
                    offset: current.offset + PAGE_SIZE,
                  }))
                }
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default ShipmentList;
